import React, { useState, useEffect } from 'react';
import api from '../../../api.js';
import { MapPin, Navigation, X } from 'lucide-react';
import { isFlagOn } from '../../../utils/dbFlags';

function LocationAssignmentModal({ isOpen, onClose, onSuccess, onMessage, vehicle, sites, currentLocation }) {
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [assignmentType, setAssignmentType] = useState('permanent');
  const [assignedAt, setAssignedAt] = useState(new Date().toISOString().split('T')[0]);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [notes, setNotes] = useState('');

  // Pre-select current site ONLY when modal opens (not on every prop change)
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (isOpen && !initialized) {
      // Check for current location from prop
      if (currentLocation?.site?.id) {
        setSelectedSiteId(currentLocation.site.id.toString());
        setAssignmentType(currentLocation.location?.assignment_type || 'permanent');
      }
      // Fallback: check vehicle.working_site_id
      else if (vehicle?.working_site_id) {
        setSelectedSiteId(vehicle.working_site_id.toString());
        setAssignmentType('permanent');
      }
      // Reset to empty if no current site
      else {
        setSelectedSiteId('');
        setAssignmentType('permanent');
      }
      // Reset other fields
      setAssignedAt(new Date().toISOString().split('T')[0]);
      setExpectedReturnDate('');
      setNotes('');
      setInitialized(true);
    }
    if (!isOpen) {
      setInitialized(false);
    }
  }, [isOpen, initialized, currentLocation, vehicle?.working_site_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSiteId) {
      onMessage && onMessage({
        title: 'Erreur',
        message: 'Veuillez sélectionner un emplacement',
        type: 'error'
      });
      return;
    }

    try {
      // Create vehicle location assignment
      const locationData = {
        org_id: vehicle?.org_id || 'ORG-A',
        vehicle_id: vehicle?.id,
        site_id: parseInt(selectedSiteId),
        assignment_type: assignmentType,
        assigned_at: assignedAt,
        expected_return_date: expectedReturnDate || null,
        is_current: 1,
        status: 'active',
        notes: notes,
      };

      // Deactivate any existing current location for this vehicle
      try {
        const existing = await api.vehicleLocation.getByVehicle(vehicle.id);
        const currentOnes = (existing.data || []).filter(l => isFlagOn(l.is_current));
        for (const loc of currentOnes) {
          await api.vehicleLocation.update(loc.id, { is_current: 0, status: 'completed', ended_at: assignedAt });
        }
      } catch (deactivateErr) {
        console.warn('Failed to deactivate previous locations:', deactivateErr.message);
      }

      await api.vehicleLocation.create(locationData);

      // Also update the vehicle's working_site_id so the overview shows the new site immediately
      try {
        await api.vehicle.update(vehicle.id, { working_site_id: parseInt(selectedSiteId) });
      } catch (updateErr) {
        console.warn('Failed to update working_site_id on vehicle:', updateErr.message);
      }

      onMessage && onMessage({
        title: 'Succès',
        message: 'Emplacement affecté avec succès',
        type: 'success'
      });
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning location:', error);
      onMessage && onMessage({
        title: 'Erreur',
        message: `Erreur lors de l'affectation: ${error.message}`,
        type: 'error'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Affecter un Emplacement</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Véhicule</h3>
            <p className="text-gray-700">{vehicle?.make} {vehicle?.model} - {vehicle?.license_plate}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site / Emplacement *</label>
            <select
              value={selectedSiteId}
              onChange={(e) => setSelectedSiteId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Sélectionner un site...</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.site_name} - {site.city} ({site.parking_capacity} places)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type d'affectation</label>
            <select
              value={assignmentType}
              onChange={(e) => setAssignmentType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="permanent">Permanent</option>
              <option value="temporary">Temporaire</option>
              <option value="maintenance">Maintenance</option>
              <option value="standby">En attente</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date d'affectation</label>
              <input
                type="date"
                value={assignedAt}
                onChange={(e) => setAssignedAt(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de retour prévue</label>
              <input
                type="date"
                value={expectedReturnDate}
                onChange={(e) => setExpectedReturnDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                disabled={assignmentType === 'permanent'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Notes additionnelles..."
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Affectation d'emplacement</p>
                <p>Le véhicule sera immédiatement rattaché au site sélectionné.</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
            >
              <Navigation className="w-5 h-5" />
              Affecter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// --- VEHICLE DETAIL VIEW (WITH TABS) ---

export default LocationAssignmentModal;
